import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  /** @type {Map<string, Promise<Element | null>>} */
  #pendingContent = new Map();

  /** Evita aperturas duplicadas por toques repetidos. */
  #isOpening = false;

  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (
      this.closest('product-card')
    );

    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );

    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card.
   * @returns {string | null}
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (
      this.closest('product-card')
    );

    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    /*
     * Desde el inicio mantiene activo el botón de vista rápida
     * en las tarjetas configuradas para usar el modal.
     */
    this.#forceQuickViewButton();

    mediaQueryLarge.addEventListener(
      'change',
      this.#closeQuickAddModal
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate,
      {
        signal: this.#cartUpdateAbortController.signal,
      }
    );

    document.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelectUpdate
    );

    /*
     * Precarga antes del clic definitivo:
     * pointerenter en escritorio,
     * pointerdown en móvil
     * y focusin para teclado.
     */
    this.addEventListener(
      'pointerenter',
      this.#prefetchQuickView
    );

    this.addEventListener(
      'pointerdown',
      this.#prefetchQuickView,
      { passive: true }
    );

    this.addEventListener(
      'focusin',
      this.#prefetchQuickView
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener(
      'change',
      this.#closeQuickAddModal
    );

    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();

    document.removeEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelectUpdate
    );

    this.removeEventListener(
      'pointerenter',
      this.#prefetchQuickView
    );

    this.removeEventListener(
      'pointerdown',
      this.#prefetchQuickView
    );

    this.removeEventListener(
      'focusin',
      this.#prefetchQuickView
    );
  }

  /**
   * Mantiene el botón del ojo cuando la tarjeta está
   * configurada para abrir siempre la vista rápida.
   *
   * @returns {boolean}
   */
  #forceQuickViewButton() {
    const forceModalButton = this.querySelector(
      '.quick-add__button--force-modal'
    );

    const chooseButton = this.querySelector(
      '.quick-add__button--choose'
    );

    /*
     * También comprobamos data-force-modal por si el botón
     * todavía no ha terminado de actualizarse.
     */
    const shouldForceModal =
      this.dataset.forceModal === 'true' ||
      forceModalButton instanceof HTMLElement;

    if (!shouldForceModal || !chooseButton) {
      return false;
    }

    this.dataset.forceModal = 'true';

    this.setAttribute(
      'data-quick-add-button',
      'choose'
    );

    return true;
  }

  /**
   * Updates quick-add button state when product variant is selected.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    if (
      event.target.closest('product-card') !==
      this.closest('product-card')
    ) {
      return;
    }

    if (this.dataset.usesSellingPlans === 'true') {
      return;
    }

    /*
     * Forever Bloom mantiene siempre el ojo y abre el modal.
     * Esto impide que Horizon lo sustituya por añadir al carrito
     * cuando se selecciona una variante de color.
     */
    if (this.#forceQuickViewButton()) {
      return;
    }

    /*
     * Horizon solo puede cambiar entre choose y add
     * cuando ambos botones existen en el HTML.
     */
    if (this.dataset.rendersBothButtons !== 'true') {
      return;
    }

    const productOptionsCount =
      this.dataset.productOptionsCount;

    const quickAddButton =
      productOptionsCount === '1'
        ? 'add'
        : 'choose';

    this.setAttribute(
      'data-quick-add-button',
      quickAddButton
    );
  };

  /**
   * Clears cached content when cart is updated.
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   *
   * @param {Element} newHtml
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById(
      'quick-add-modal-content'
    );

    if (!modalContent) return;

    const variantPicker =
      /** @type {VariantPicker | null} */ (
        modalContent.querySelector('variant-picker')
      );

    if (!variantPicker) return;

    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Comienza a descargar el producto antes del clic.
   */
  #prefetchQuickView = () => {
    if (this.dataset.usesSellingPlans === 'true') {
      return;
    }

    const currentUrl = this.productPageUrl;

    if (!currentUrl) return;

    if (this.#cachedContent.has(currentUrl)) {
      return;
    }

    if (this.#pendingContent.has(currentUrl)) {
      return;
    }

    void this.#loadProductGrid(currentUrl).catch(() => {
      /*
       * Si la precarga falla, el clic volverá a intentarlo
       * normalmente.
       */
    });
  };

  /**
   * Reutiliza contenido guardado o una petición que ya esté activa.
   *
   * @param {string} currentUrl
   * @returns {Promise<Element | null>}
   */
  #loadProductGrid(currentUrl) {
    const cachedContent =
      this.#cachedContent.get(currentUrl);

    if (cachedContent) {
      return Promise.resolve(cachedContent);
    }

    const pendingRequest =
      this.#pendingContent.get(currentUrl);

    if (pendingRequest) {
      return pendingRequest;
    }

    const request = this.fetchProductPage(currentUrl)
      .then((html) => {
        if (!html) return null;

        /*
         * Horizon utiliza data-product-grid-content.
         * La sección de Forever Bloom utiliza fb-product-main.
         */
        const gridElement =
          html.querySelector(
            '[data-product-grid-content]'
          ) ||
          html.querySelector(
            'fb-product-main[data-product-id]'
          );

        if (!gridElement) return null;

        const productGrid =
          /** @type {Element} */ (
            gridElement.cloneNode(true)
          );

        this.#cachedContent.set(
          currentUrl,
          productGrid
        );

        return productGrid;
      })
      .finally(() => {
        this.#pendingContent.delete(currentUrl);
      });

    this.#pendingContent.set(
      currentUrl,
      request
    );

    return request;
  }

  /**
   * Muestra el estado de carga y bloquea toques repetidos.
   *
   * @param {HTMLButtonElement | null} button
   * @param {boolean} isOpening
   */
  #setOpeningState(button, isOpening) {
    if (!(button instanceof HTMLButtonElement)) {
      return;
    }

    button.disabled = isOpening;

    button.toggleAttribute(
      'aria-busy',
      isOpening
    );

    button.classList.toggle(
      'is-loading',
      isOpening
    );
  }

  /**
   * Handles quick-add button click.
   *
   * @param {Event} event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#isOpening) return;

    const currentUrl = this.productPageUrl;

    const button =
      event.target instanceof Element
        ? event.target.closest(
            'button.quick-add__button'
          )
        : null;

    if (!currentUrl) return;

    if (
      this.dataset.usesSellingPlans === 'true'
    ) {
      window.location.href = currentUrl;
      return;
    }

    /*
     * Reafirma el botón de vista rápida antes de abrir.
     */
    this.#forceQuickViewButton();

    this.#isOpening = true;

    this.#setOpeningState(
      button instanceof HTMLButtonElement
        ? button
        : null,
      true
    );

    try {
      const productGrid =
        await this.#loadProductGrid(currentUrl);

      /*
       * Nunca abre un modal vacío.
       */
      if (!productGrid) {
        if (this.isConnected) {
          window.location.href = currentUrl;
        }

        return;
      }

      const freshContent =
        /** @type {Element} */ (
          productGrid.cloneNode(true)
        );

      const didRender =
        await this.updateQuickAddModal(
          freshContent
        );

      if (!didRender) {
        window.location.href = currentUrl;
        return;
      }

      this.#updateVariantPicker(productGrid);
      this.#openQuickAddModal();
    } catch (error) {
      console.warn(
        '[quick-add] Could not render the quick view:',
        error
      );

      if (this.isConnected) {
        window.location.href = currentUrl;
      }
    } finally {
      this.#isOpening = false;

      this.#setOpeningState(
        button instanceof HTMLButtonElement
          ? button
          : null,
        false
      );

      /*
       * Evita que cualquier actualización posterior de Horizon
       * vuelva a convertir el ojo en el icono de añadir.
       */
      this.#forceQuickViewButton();
    }
  };

  #resetScroll() {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(dialogComponent instanceof QuickAddDialog)
    ) {
      return;
    }

    const productDetails =
      dialogComponent.querySelector(
        '.fb-product-main__info, .product-details'
      );

    const productMedia =
      dialogComponent.querySelector(
        '.fb-product-main__media-column, .product-information__media'
      );

    productDetails?.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant',
    });

    productMedia?.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant',
    });
  }

  /**
   * @param {QuickAddDialog} dialogComponent
   */
  #stayVisibleUntilDialogCloses(
    dialogComponent
  ) {
    this.toggleAttribute(
      'stay-visible',
      true
    );

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        this.toggleAttribute(
          'stay-visible',
          false
        );
      },
      {
        once: true,
      }
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(dialogComponent instanceof QuickAddDialog)
    ) {
      return;
    }

    this.#stayVisibleUntilDialogCloses(
      dialogComponent
    );

    dialogComponent.showDialog();

    /*
     * Espera a que comience la animación para restablecer
     * correctamente el scroll.
     */
    const dialog =
      dialogComponent.refs?.dialog;

    if (!dialog) return;

    dialog.addEventListener(
      'animationstart',
      this.#resetScroll.bind(this),
      {
        once: true,
      }
    );
  };

  #closeQuickAddModal = () => {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(dialogComponent instanceof QuickAddDialog)
    ) {
      return;
    }

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content.
   *
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    /*
     * Referencia local para impedir que el finally de una
     * petición anterior elimine el controlador nuevo.
     */
    const controller =
      new AbortController();

    this.#abortController?.abort();
    this.#abortController = controller;

    try {
      const response = await fetch(
        productPageUrl,
        {
          signal: controller.signal,
          credentials: 'same-origin',
          cache: 'default',
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`
        );
      }

      const responseText =
        await response.text();

      return new DOMParser().parseFromString(
        responseText,
        'text/html'
      );
    } catch (error) {
      if (
        error instanceof DOMException &&
        error.name === 'AbortError'
      ) {
        return null;
      }

      throw error;
    } finally {
      if (
        this.#abortController === controller
      ) {
        this.#abortController = null;
      }
    }
  }

  /**
   * Re-renders the product inside the modal.
   *
   * @param {Element} productGrid
   * @returns {Promise<boolean>}
   */
  async updateQuickAddModal(productGrid) {
    const modalContent =
      document.getElementById(
        'quick-add-modal-content'
      );

    if (!productGrid || !modalContent) {
      return false;
    }

    const isForeverBloomProduct =
      productGrid.matches('fb-product-main');

    if (isForeverBloomProduct) {
      this.#prepareForeverBloomProduct(
        productGrid
      );

      modalContent.classList.add(
        'quick-add-modal__content--fb'
      );

      modalContent.removeAttribute(
        'view-event-payload'
      );

      modalContent.replaceChildren(
        productGrid
      );

      return true;
    }

    modalContent.classList.remove(
      'quick-add-modal__content--fb'
    );

    if (isMobileBreakpoint()) {
      const productDetails =
        productGrid.querySelector(
          '.product-details'
        );

      const productFormComponent =
        productGrid.querySelector(
          'product-form-component'
        );

      const variantPicker =
        productGrid.querySelector(
          'variant-picker'
        );

      const productPrice =
        productGrid.querySelector(
          'product-price'
        );

      const productTitle =
        document.createElement('a');

      productTitle.textContent =
        this.dataset.productTitle || '';

      productTitle.href =
        this.productPageUrl;

      const productHeader =
        document.createElement('div');

      productHeader.classList.add(
        'product-header'
      );

      productHeader.appendChild(
        productTitle
      );

      if (productPrice) {
        productHeader.appendChild(
          productPrice
        );
      }

      productGrid.appendChild(
        productHeader
      );

      if (variantPicker) {
        productGrid.appendChild(
          variantPicker
        );
      }

      if (productFormComponent) {
        productGrid.appendChild(
          productFormComponent
        );
      }

      if (
        this.dataset.forceModal === 'true'
      ) {
        const viewDetailsLink =
          document.createElement('a');

        viewDetailsLink.classList.add(
          'quick-add-modal__mobile-details'
        );

        viewDetailsLink.href =
          this.productPageUrl;

        viewDetailsLink.textContent =
          this.dataset.viewDetailsLabel ||
          'View more details';

        productGrid.appendChild(
          viewDetailsLink
        );
      }

      productDetails?.remove();
    }

    const payload =
      productGrid.getAttribute(
        'view-event-payload'
      ) || '';

    modalContent.setAttribute(
      'view-event-payload',
      payload
    );

    morph(
      modalContent,
      productGrid
    );

    this.#syncVariantSelection(
      modalContent
    );

    return true;
  }

  /**
   * Prepares the Forever Bloom product section for
   * use inside the Horizon quick-view dialog.
   *
   * @param {Element} productGrid
   */
  #prepareForeverBloomProduct(
    productGrid
  ) {
    productGrid.setAttribute(
      'data-update-url',
      'false'
    );

    productGrid.classList.add(
      'fb-product-main--quick-view'
    );

    productGrid
      .querySelector(
        '.fb-product-main__breadcrumbs'
      )
      ?.remove();

    productGrid
      .querySelector(
        '.fb-product-main__detail'
      )
      ?.remove();

    productGrid
      .querySelector('[data-lightbox]')
      ?.remove();

    productGrid
      .querySelector(
        '[data-gallery-zoom]'
      )
      ?.remove();

    productGrid
      .querySelector(
        '.fb-product-main__dynamic-checkout'
      )
      ?.remove();

    productGrid
      .querySelector(
        '.fb-product-main__inventory'
      )
      ?.remove();

    productGrid
      .querySelectorAll(
        [
          '.fb-product-main__thumbnails',
          '[data-gallery-thumbnails]',
          '[data-gallery-thumbnail]',
          '.thumbnail-list',
        ].join(', ')
      )
      .forEach((thumbnail) => {
        thumbnail.remove();
      });

    const info =
      productGrid.querySelector(
        '.fb-product-main__info'
      );

    if (
      info &&
      !info.querySelector(
        '.fb-product-main__quick-view-details'
      )
    ) {
      const detailsLink =
        document.createElement('a');

      detailsLink.className =
        'fb-product-main__quick-view-details';

      detailsLink.href =
        this.productPageUrl;

      detailsLink.textContent =
        this.dataset.viewDetailsLabel ||
        'Ver todos los detalles';

      detailsLink.setAttribute(
        'aria-label',
        detailsLink.textContent
      );

      const arrow =
        document.createElement('span');

      arrow.setAttribute(
        'aria-hidden',
        'true'
      );

      arrow.textContent = '›';

      detailsLink.appendChild(arrow);
      info.appendChild(detailsLink);
    }

    this.#namespaceQuickViewIds(
      productGrid
    );
  }

  /**
   * Avoids duplicate IDs inside the quick-view dialog.
   *
   * @param {Element} root
   */
  #namespaceQuickViewIds(root) {
    const prefix =
      `QuickView-${this.dataset.productId || 'Product'}-`;

    const elements = [
      root,
      ...root.querySelectorAll('*'),
    ];

    const idMap = new Map();

    for (const element of elements) {
      if (
        !(element instanceof HTMLElement) ||
        !element.id
      ) {
        continue;
      }

      const oldId = element.id;
      const newId = `${prefix}${oldId}`;

      idMap.set(oldId, newId);
      element.id = newId;
    }

    const referenceAttributes = [
      'for',
      'aria-controls',
      'aria-labelledby',
      'aria-describedby',
    ];

    for (const element of elements) {
      if (!(element instanceof HTMLElement)) {
        continue;
      }

      for (
        const attribute of referenceAttributes
      ) {
        const value =
          element.getAttribute(attribute);

        if (!value) continue;

        const updatedValue = value
          .split(/\s+/)
          .map((token) => {
            return idMap.get(token) || token;
          })
          .join(' ');

        element.setAttribute(
          attribute,
          updatedValue
        );
      }

      const href =
        element.getAttribute('href');

      if (href?.startsWith('#')) {
        const targetId = href.slice(1);

        if (idMap.has(targetId)) {
          element.setAttribute(
            'href',
            `#${idMap.get(targetId)}`
          );
        }
      }
    }
  }

  /**
   * Syncs selected variant from the card to the modal.
   *
   * @param {Element} modalContent
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId =
      this.#getSelectedVariantId();

    if (!selectedVariantId) return;

    const modalInputs =
      modalContent.querySelectorAll(
        'input[type="radio"][data-variant-id]'
      );

    for (const input of modalInputs) {
      if (
        input instanceof HTMLInputElement &&
        input.dataset.variantId ===
          selectedVariantId &&
        !input.checked
      ) {
        input.checked = true;

        input.dispatchEvent(
          new Event('change', {
            bubbles: true,
          })
        );

        break;
      }
    }
  }
}

if (
  !customElements.get(
    'quick-add-component'
  )
) {
  customElements.define(
    'quick-add-component',
    QuickAddComponent
  );
}

class QuickAddDialog extends DialogComponent {
  #abortController =
    new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.handleCartUpdate,
      {
        signal:
          this.#abortController.signal,
      }
    );

    this.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );

    this.addEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();

    this.removeEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );

    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );
  }

  /**
   * Closes the dialog after a successful cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;

        this.closeDialog();
      })
      .catch((error) => {
        if (
          error?.name !== 'AbortError'
        ) {
          console.warn(
            '[quick-add] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Updates product links after a variant change.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        const anchorElement =
          /** @type {HTMLAnchorElement | null} */ (
            html.querySelector(
              '.view-product-title a'
            )
          );

        const viewMoreDetailsLink =
          /** @type {HTMLAnchorElement | null} */ (
            this.querySelector(
              '.view-product-title a'
            )
          );

        const mobileProductTitle =
          /** @type {HTMLAnchorElement | null} */ (
            this.querySelector(
              '.product-header a'
            )
          );

        const mobileDetailsLink =
          /** @type {HTMLAnchorElement | null} */ (
            this.querySelector(
              '.quick-add-modal__mobile-details'
            )
          );

        if (!anchorElement) return;

        if (viewMoreDetailsLink) {
          viewMoreDetailsLink.href =
            anchorElement.href;
        }

        if (mobileProductTitle) {
          mobileProductTitle.href =
            anchorElement.href;
        }

        if (mobileDetailsLink) {
          mobileDetailsLink.href =
            anchorElement.href;
        }
      })
      .catch((error) => {
        if (
          error?.name !== 'AbortError'
        ) {
          console.warn(
            '[quick-add] Event promise rejected:',
            error
          );
        }
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();

    /*
     * Patch para una congelación de interfaz
     * que afecta a ciertas versiones de iOS 16.
     */
    if (
      !iosVersion ||
      iosVersion.major >= 17 ||
      (
        iosVersion.major === 16 &&
        iosVersion.minor >= 4
      )
    ) {
      return;
    }

    requestAnimationFrame(() => {
      const grid =
        /** @type {HTMLElement | null} */ (
          document.querySelector(
            '#ResultsList [product-grid-view]'
          )
        );

      if (!grid) return;

      const currentWidth =
        grid.getBoundingClientRect().width;

      grid.style.width =
        `${currentWidth - 1}px`;

      requestAnimationFrame(() => {
        grid.style.width = '';
      });
    });
  };
}

if (
  !customElements.get(
    'quick-add-dialog'
  )
) {
  customElements.define(
    'quick-add-dialog',
    QuickAddDialog
  );
}